import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

export enum ConnectionStatus {
    Connecting,
    Connected,
    Disconnected,
    Reconnecting
}

/**
 * Interface para estandarizar los listeners de mensajes.
 * Ayuda a gestionar y remover listeners de forma segura.
 */
interface MessageListener {
    id: number;
    callback: (data: any) => void;
}

@Injectable({
    providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
    private socket: WebSocket | null = null;
    private listeners: MessageListener[] = [];
    private listenerIdCounter = 0;
    private connectionStatus$ = new BehaviorSubject<ConnectionStatus>(ConnectionStatus.Disconnected);

   
    private reconnectInterval = 1000; // Inicia en 1 segundo
    private maxReconnectInterval = 30000; // MÃ¡ximo 30 segundos
    private reconnectTimeoutId: any;

    constructor() {
        this.connect();
    }

    public getStatus(): Observable<ConnectionStatus> {
        return this.connectionStatus$.asObservable();
    }

    public connect(): void {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            return;
        }

        this.reconnectInterval = 1000;
        this.connectionStatus$.next(ConnectionStatus.Connecting); 
        this.socket = new WebSocket(environment.apiConfig!.websocket);

        this.socket.onopen = (event) => this.handleOpen(event);
        this.socket.onmessage = (event) => this.handleMessage(event);
        this.socket.onclose = (event) => this.handleClose(event);
        this.socket.onerror = (event) => this.handleError(event);
    }

    /**
     * Manejador para el evento 'onopen'.
     * Se ejecuta cuando la conexiÃ³n se establece correctamente.
     */
    private handleOpen(event: Event): void {
        this.connectionStatus$.next(ConnectionStatus.Connected); 
        // Al conectar exitosamente, reseteamos el intervalo de reconexiÃ³n.
        this.reconnectInterval = 1000;
        // Si habÃ­a un intento de reconexiÃ³n programado, lo cancelamos.
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }
    }

    /**
     * Manejador para el evento 'onmessage'.
     * Procesa los mensajes recibidos del servidor y los notifica a los listeners.
     */
    private handleMessage(event: MessageEvent): void {
        try {
            const data = JSON.parse(event.data); 
            // Notifica a todos los componentes que estÃ¡n escuchando
            this.listeners.forEach(listener => listener.callback(data));
        } catch (error) { 
            // Si el mensaje no es JSON, lo pasamos tal cual
            this.listeners.forEach(listener => listener.callback(event.data));
        }
    }

    /**
     * Manejador para el evento 'onclose'.
     * Se ejecuta cuando la conexiÃ³n se cierra. Inicia el proceso de reconexiÃ³n.
     */
    private handleClose(event: CloseEvent): void {
        console.warn(`ConexiÃ³n WebSocket cerrada. CÃ³digo: ${event.code}. RazÃ³n: ${event.reason}`);
        this.connectionStatus$.next(ConnectionStatus.Disconnected);
        // No intentar reconectar si el cierre fue normal (cÃ³digo 1000)
        if (event.code !== 1000) {
            this.scheduleReconnect();
        }
    }

    /**
     * Manejador para el evento 'onerror'.
     * Se ejecuta cuando ocurre un error en la conexiÃ³n.
     */
    private handleError(event: Event): void {
        console.error('Error en WebSocket:', event);
        this.connectionStatus$.next(ConnectionStatus.Disconnected);
    }

    /**
     * Programa el siguiente intento de reconexiÃ³n usando una estrategia
     * de "exponential backoff" para no sobrecargar el servidor.
     */
    private scheduleReconnect(): void {
        this.connectionStatus$.next(ConnectionStatus.Reconnecting);
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        } 

        this.reconnectTimeoutId = setTimeout(() => {
            this.connect();
            // Duplicamos el intervalo para el siguiente intento, hasta un mÃ¡ximo.
            this.reconnectInterval = Math.min(this.maxReconnectInterval, this.reconnectInterval * 2);
        }, this.reconnectInterval);
    }

    /**
     * EnvÃ­a datos al servidor WebSocket.
     * @param data Los datos a enviar. Se convertirÃ¡n a formato JSON.
     */
    public sendMessage(data: any): void {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) { 
            this.socket.send(JSON.stringify(data));
        } else {
            console.error('No se puede enviar el mensaje. El WebSocket no estÃ¡ conectado.');
        }
    }

    /**
     * Registra una funciÃ³n callback para que sea llamada cuando se reciba un mensaje.
     * @param callback La funciÃ³n que manejarÃ¡ los datos recibidos.
     * @returns El ID del listener, para poder removerlo despuÃ©s.
     */
    public addMessageListener(callback: (data: any) => void): number {
        this.listenerIdCounter++;
        this.listeners.push({ id: this.listenerIdCounter, callback });
        return this.listenerIdCounter;
    }

    public emitMockMessage(message: any): void { 
        this.listeners.forEach(listener => listener.callback(message));
    }

    /**
     * Elimina un listener de mensajes usando su ID.
     * @param listenerId El ID retornado por `addMessageListener`.
     */
    public removeMessageListener(listenerId: number): void {
        this.listeners = this.listeners.filter(listener => listener.id !== listenerId); 
    }

    /**
     * Cierra la conexiÃ³n WebSocket de forma manual y limpia los recursos.
     */
    public closeConnection(): void {
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }
        if (this.socket) {
            // Usamos el cÃ³digo 1000 para indicar un cierre normal y evitar la reconexiÃ³n.
            this.socket.close(1000, 'Cierre manual por el cliente');
            this.socket = null;
        }
        this.connectionStatus$.next(ConnectionStatus.Disconnected);
    }

    ngOnDestroy(): void {
        this.closeConnection();
        this.connectionStatus$.complete();
    }
}
